import { inject, Injectable, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Apollo, QueryRef } from 'apollo-angular';
import { Observable, lastValueFrom, map } from 'rxjs';
import {
    GetRangeProductsQuery,
    MarketplaceInternalProductConnectInput,
    MarketplaceInternalProductCreateInput,
    MarketplacePlanCreateInput,
    MarketplaceProductRoutingAddInput,
    MarketplaceProductRoutingDeleteInput,
    MarketplaceProductRoutingUpdateInput,
    MarketplaceProductUpdateInput,
    Product,
    ProductInfoFragment,
    ProductSearchDocument,
    ProductSearchQuery,
    ValidateRangeProductQuery,
} from 'src/__generated__/graphql';
import { internalProductAdapter } from 'src/app/adapters/marketplace/internalProductsAdapter';
import { ErrorSnackbarComponent } from 'src/app/layouts/ui/snackbars/snackbars.component';
import { internalProduct } from 'src/app/models/marketplace/internalProducts';
import { RangeProductQueries } from '../graphql/rangeProduct-queries';
import { ProductQueries } from '../graphql/product-queries';
import { ProductDetailComponent } from 'src/app/views/marketplace/components/products/product-detail/product-detail.component';
import {
    ConnectProductsGQL,
    CreateInternalProductGQL,
    DeleteInternalProductGQL,
    InternalProductSearchGQL,
    ProductSearchGQL,
    UpdateInternalProductGQL,
} from 'src/__generated__/services';

@Injectable()
export class MarketplaceProductService {
    private apollo: Apollo = inject(Apollo);
    private snackbar = inject(MatSnackBar);
    private dialog = inject(MatDialog);

    connectProductsGql = inject(ConnectProductsGQL);
    getProductsGql = inject(ProductSearchGQL);
    getInternalProductGql = inject(InternalProductSearchGQL);
    createInternalProductGql = inject(CreateInternalProductGQL);
    internalProductSearchGql = inject(InternalProductSearchGQL);
    deleteInternalProductGql = inject(DeleteInternalProductGQL);
    updateInternalProductGql = inject(UpdateInternalProductGQL);

    constructor(private internalProductsAdaptermap: internalProductAdapter) {}

    public getInternalProducts = (
        filter: any,
    ): Observable<Array<internalProduct>> => {
        return this.apollo
            .query<any>({
                query: this.internalProductSearchGql.document,
                fetchPolicy: 'no-cache',
                variables: filter,
            })
            .pipe(
                map(({ data }) =>
                    this.internalProductsAdaptermap.mapInternalProducts(data),
                ),
            );
    };
    public getProductsQuery = (filter: any): QueryRef<ProductSearchQuery> => {
        return this.apollo.watchQuery<ProductSearchQuery>({
            query: ProductSearchDocument,
            variables: filter,
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        });
    };
    public getProductsProvider = (
        filter: any,
    ): Observable<Apollo.QueryResult<ProductSearchQuery>> => {
        return this.apollo.query<ProductSearchQuery>({
            query: ProductQueries.PRODUCTS_PROVIDER,
            variables: filter,
            fetchPolicy: 'cache-first',
            errorPolicy: 'all',
        });
    };
    public getProducts = (filter: any): Observable<Product[]> => {
        return this.apollo
            .query<ProductSearchQuery>({
                query: ProductQueries.PRODUCTS,
                variables: filter,
            })
            .pipe(
                map((result) => {
                    return result.data?.marketplaceProductSearch
                        .items as Product[];
                }),
            );
    };

    public editProduct = (input: MarketplaceProductUpdateInput) => {
        this.apollo
            .mutate({
                mutation: ProductQueries.SAVE_PRODUCT,
                variables: input,
            })
            .subscribe((result: any) => {
                if (result) this.snackbar.open('Product Updated', 'Close');
                else
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                        data: result.errors,
                    });
            });
    };

    public bundleEditProduct = (input: MarketplaceProductUpdateInput) => {
        return this.apollo.mutate({
            mutation: ProductQueries.SAVE_PRODUCT,
            variables: { input: input },
        });
    };

    public toggleProduct = (
        product: Pick<Product, 'id' | 'enabled' | 'subscriptionAvailability'>,
    ) => {
        return this.apollo
            .mutate({
                mutation: ProductQueries.SAVE_PRODUCT,
                variables: {
                    input: {
                        ids: product.id,
                        enabled: !product.enabled,
                        subscriptionAvailability:
                            !!product.subscriptionAvailability,
                    },
                },
            })
            .subscribe((result: any) => {
                if (result) this.snackbar.open('Product Updated', 'Close');
                else
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                        data: result.errors,
                    });
            });
    };

    public openProductDetails = (
        product: WritableSignal<ProductInfoFragment>,
    ) => {
        this.dialog.open(ProductDetailComponent, {
            width: '70%',
            height: '90%',
            panelClass: '',
            position: { top: '50px' },
            data: { product: product },
        });
    };

    connectProduct = (
        input: MarketplaceInternalProductConnectInput,
    ): Observable<Apollo.MutateResult<any>> => {
        return this.apollo.mutate({
            mutation: this.connectProductsGql.document,
            variables: {
                input: input,
            },
        });
    };

    createPlan = (input: MarketplacePlanCreateInput) => {
        this.apollo
            .mutate({
                mutation: ProductQueries.SAVE_PLAN,
                variables: {
                    input: input,
                },
            })
            .subscribe((result: any) => {
                if (result) this.snackbar.open('Plan Created', 'Close');
                else
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                        data: { errors: result.errors.concat() },
                    });
            });
    };

    createInternalProduct = (
        input: MarketplaceInternalProductCreateInput,
        productId: string,
    ) => {
        this.apollo
            .mutate({
                mutation: this.createInternalProductGql.document,
                variables: {
                    input: input,
                },
            })
            .subscribe(({ data }: any) => {
                this.connectProduct({
                    productIds: [productId],
                    internalProductId:
                        data.marketplace.internalProductCreate.id,
                });
            });
    };

    public getInternalProductFromId = (ids: string[] | string) =>
        lastValueFrom(
            this.getInternalProductGql
                .fetch({
                    variables: {
                        where: {
                            internalProductIds: {
                                in: typeof ids === 'string' ? [ids] : ids,
                            },
                        },
                    },
                })
                .pipe(
                    map(
                        (result) =>
                            result.data?.marketplaceInternalProductSearch.items,
                    ),
                ),
        );

    public getProductFromId = (ids: string[] | string) =>
        lastValueFrom(
            this.getProductsGql
                .fetch({
                    variables: {
                        where: {
                            productIds: {
                                in: typeof ids === 'string' ? [ids] : ids,
                            },
                        },
                    },
                })
                .pipe(
                    map(
                        (result) => result.data?.marketplaceProductSearch.items,
                    ),
                ),
        );

    public getRangeProductQuery = (input: any) => {
        return this.apollo.watchQuery<GetRangeProductsQuery>({
            query: RangeProductQueries.RANGEPRODUCTS,
            variables: input,
        });
    };

    public getRoutingQuery = (input: any) => {
        return this.apollo.watchQuery<ValidateRangeProductQuery>({
            query: ProductQueries.ROUTING,
            variables: input,
        });
    };

    public createRoute = (input: MarketplaceProductRoutingAddInput) => {
        return this.apollo.mutate<any>({
            mutation: ProductQueries.ADDROUTE,
            variables: input,
            errorPolicy: 'all',
        });
    };

    public deleteRoute = (input: MarketplaceProductRoutingDeleteInput) => {
        return this.apollo.mutate<any>({
            mutation: ProductQueries.DELETEROUTE,
            variables: { input: input },
            errorPolicy: 'all',
        });
    };

    public updateRoute = (input: MarketplaceProductRoutingUpdateInput) => {
        return this.apollo.mutate<any>({
            mutation: ProductQueries.UPDATEROUTE,
            variables: { input: input },
            errorPolicy: 'all',
        });
    };
}
